import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { Button } from 'src/components/shared/Button'
import { FormikSwitch } from 'src/components/shared/form/formik/FormikSwitch'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { RemoteConfigValueType } from 'src/constants/remote-config/remoteConfig'
import type { RemoteConfigFormValues } from 'src/interfaces/remoteConfig'

const JSON_HINT = 'An object or an array. The app reads it as is; Format tidies the spacing.'

interface Props {
  isReadOnly: boolean
  onFormat(): void
}

/** The Value field follows the type: text area, number field, true/false switch, or a JSON code area. */
const RemoteConfigValueEditorComponent = ({ isReadOnly, onFormat }: Props) => {
  const { values, errors, touched } = useFormikContext<RemoteConfigFormValues>()
  const hasValueError = Boolean(touched.valueText && errors.valueText)

  if (values.type === RemoteConfigValueType.Boolean) {
    return (
      <div css={tw`flex flex-col gap-1`}>
        <Typography variant="h6" css={tw`text-shades-400`}>
          Value *
        </Typography>
        <FormikSwitch<RemoteConfigFormValues>
          name="valueBoolean"
          label={values.valueBoolean ? 'true' : 'false'}
          disabled={isReadOnly}
        />
      </div>
    )
  }

  if (values.type === RemoteConfigValueType.Number) {
    return (
      <FormikTextField<RemoteConfigFormValues>
        name="valueText"
        label="Value *"
        placeholder="0"
        disabled={isReadOnly}
        inputProps={{ inputMode: 'decimal' }}
        css={tw`max-w-[240px]`}
      />
    )
  }

  if (values.type === RemoteConfigValueType.String) {
    return (
      <FormikTextArea<RemoteConfigFormValues>
        name="valueText"
        label="Value *"
        placeholder=""
        minRows={3}
        disabled={isReadOnly}
        fullWidth
      />
    )
  }

  return (
    <div css={tw`flex flex-col gap-1`}>
      <FormikTextArea<RemoteConfigFormValues>
        name="valueText"
        label="Value *"
        placeholder="{ }"
        minRows={9}
        disabled={isReadOnly}
        inputProps={{ spellCheck: false }}
        fullWidth
        css={tw`[& textarea]:(font-mono text-xs)`}
      />
      {isReadOnly ? null : (
        <div css={tw`flex flex-row items-start justify-between gap-4`}>
          <Typography variant="body2" css={tw`text-shades-500`}>
            {hasValueError ? '' : JSON_HINT}
          </Typography>
          <Button
            size="small"
            color="grey"
            onClick={onFormat}
            data-testid="remote-config-format-button"
          >
            Format
          </Button>
        </div>
      )}
    </div>
  )
}

export const RemoteConfigValueEditor = memo(RemoteConfigValueEditorComponent)
